/**
 * Componente para exibir mensagens de link com preview
 * 
 * Visual estilo WhatsApp Web com thumbnail, título, descrição
 * e botão de play para vídeos (YouTube, Vimeo)
 */

import { ExternalLink, Play, Link2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStorageUrl } from '@/lib/storage';

interface LinkMessageBubbleProps {
  /** URL do link */
  url: string;
  /** Título do preview (og:title) */
  title?: string | null;
  /** Descrição do preview (og:description) */
  description?: string | null;
  /** URL da thumbnail/imagem de preview */
  thumbnailUrl?: string | null;
  /** Mensagem de texto adicional */
  message?: string | null;
  /** Direção da mensagem */
  direction: 'inbound' | 'outbound';
}

/**
 * Verifica se a URL é de um site de vídeo (YouTube, Vimeo)
 */
function isVideoUrl(url: string): boolean {
  const videoHosts = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'dailymotion.com',
    'twitch.tv',
  ];
  
  try {
    const urlObj = new URL(url);
    return videoHosts.some(host => urlObj.hostname.includes(host));
  } catch {
    return false;
  }
}

/**
 * Extrai o hostname da URL para exibição
 */
function extractHostname(url: string): string {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace('www.', '');
  } catch {
    return url;
  }
}

export function LinkMessageBubble({
  url,
  title,
  description,
  thumbnailUrl,
  message,
  direction,
}: LinkMessageBubbleProps) {
  const isOutbound = direction === 'outbound';
  const isVideo = isVideoUrl(url);
  const hostname = extractHostname(url);
  
  // Resolve URL da thumbnail (pode ser caminho do storage ou URL externa)
  const resolvedThumbnailUrl = thumbnailUrl 
    ? getStorageUrl(thumbnailUrl) || thumbnailUrl 
    : null;

  // Handler para abrir link em nova aba
  const handleClick = () => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex flex-col gap-1 max-w-[168px]">
      {/* Card do link com preview - reduzido 40% */}
      <div
        onClick={handleClick}
        className={cn(
          'rounded-lg overflow-hidden cursor-pointer transition-opacity hover:opacity-90',
          !isOutbound && 'bg-background'
        )}
        style={isOutbound ? { backgroundColor: 'hsl(var(--chat-bubble-inbound) / 0.15)' } : undefined}
      >
        {/* Área da thumbnail */}
        {resolvedThumbnailUrl ? (
          <div className="relative">
            <img
              src={resolvedThumbnailUrl}
              alt={title || 'Preview'}
              className="w-full h-[84px] object-cover"
              onError={(e) => {
                // Se falhar ao carregar imagem, esconde a área
                (e.target as HTMLImageElement).parentElement!.style.display = 'none';
              }}
            />
            {/* Botão de play para vídeos */}
            {isVideo && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                <div className="bg-destructive rounded-full p-2 shadow-lg">
                  <Play className="h-4 w-4 text-white fill-white" />
                </div>
              </div>
            )}
          </div>
        ) : (
          // Fallback quando não há thumbnail
          <div 
            className={cn(
              'w-full h-[48px] flex items-center justify-center',
              isOutbound ? 'bg-black/5' : 'bg-muted'
            )}
          >
            {isVideo ? (
              <Play 
                className={cn('h-6 w-6', !isOutbound && 'text-muted-foreground')} 
              style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
              />
            ) : (
              <Link2 
                className={cn('h-6 w-6', !isOutbound && 'text-muted-foreground')} 
              style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
              />
            )}
          </div>
        )}

        {/* Informações do link - cores fixas WhatsApp para outbound */}
        <div className="p-1.5 space-y-0.5">
          {/* Título */}
          {title && (
            <p 
              className={cn(
                'text-[10px] font-medium line-clamp-2',
                !isOutbound && 'text-foreground'
              )}
              style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
            >
              {title}
            </p>
          )}
          
          {/* Descrição */}
          {description && (
            <p 
              className={cn(
                'text-[9px] line-clamp-2',
                !isOutbound && 'text-muted-foreground'
              )}
              style={isOutbound ? { color: 'hsl(var(--chat-text-secondary))' } : undefined}
            >
              {description}
            </p>
          )}
          
          {/* URL/Hostname */}
          <div className="flex items-center gap-0.5 pt-0.5">
            <ExternalLink 
              className={cn(
                'h-2 w-2 flex-shrink-0',
                !isOutbound && 'text-muted-foreground'
              )} 
              style={isOutbound ? { color: 'hsl(200 98% 36%)' } : undefined}
            />
            <span 
              className={cn(
                'text-[10px] truncate',
                !isOutbound && 'text-blue-500'
              )}
              style={isOutbound ? { color: 'hsl(200 98% 36%)' } : undefined}
            >
              {hostname}
            </span>
          </div>
        </div>
      </div>

      {/* Mensagem adicional (abaixo do card) */}
      {message && (
        <p 
          className={cn(
            'text-sm whitespace-pre-wrap break-words',
            !isOutbound && 'text-foreground'
          )}
          style={isOutbound ? { color: 'hsl(var(--chat-text-primary))' } : undefined}
        >
          {message}
        </p>
      )}
    </div>
  );
}
